// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.

import Vue from 'vue';
import App from './App';
import Router from 'vue-router';
import dataResource from '@/util/resources';
import Vector from '@dash/components/Common/Vector';
import 'vue-awesome/icons';
import debounce from 'v-debounce';
import BootstrapVue from 'bootstrap-vue';
import Icon from 'vue-awesome/components/Icon';
import lodash from 'lodash';
import VuejsDialog from 'vuejs-dialog';
import Datepicker from 'vuejs-datepicker';
import VueTour from 'vue-tour';
import VueClipboard from 'vue-clipboard2';
import VueLazyLoad from 'vue-lazyload';
import vSelect from 'vue-select';
import VueMoment from 'vue-moment';
import Vue2Filters from 'vue2-filters';
import Toasted from 'vue-toasted';
import Timeago from 'vue-timeago';
import Amplify, {Hub} from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';
import Analytics from '@aws-amplify/analytics';
import JsonCSV from 'vue-json-csv';

// TODO: Refactor into User Management Package
import userUtil from './util/user.js';
import { MODULE_INFO } from '@/config/constants';

const region = process.env.REGION;

const appId = process.env.HUB_APP_ID;
const analyticsConfig = {
    AWSPinpoint: {
        // Amazon Pinpoint App Client ID
        appId: appId,
        // Amazon service region
        region: region,
        mandatorySignIn: false
    }
};

dataResource.payload().then(res => {
    Analytics.updateEndpoint({
        address: res.email,
        attributes: {
            // Custom attributes that your app reports to Amazon Pinpoint. You can use these attributes as selection criteria when you create a segment.
            position: []
        },
        channelType: 'APNS', // The channel type. Valid values: APNS, GCM

        metrics: {
            // Custom metrics that your app reports to Amazon Pinpoint.
        },
        /** Indicates whether a user has opted out of receiving messages with one of the following values:
         * ALL - User has opted out of all messages.
         * NONE - Users has not opted out and receives all messages.
         */
        optOut: 'ALL',
        // Customized userId
        userId: res.given_name + '' + res.family_name,
        // User attributes
        userAttributes: {
            user: [res.email],
            role: [res.auth.role],
            companyId: res.auth.customerId
                ? [res.auth.customerId] : (res.auth.role === 'sentient')
                    ? ['453c2512-fea2-5904-9951-c55a8e672050'] : ['unknown'],
        }
    });
});

Analytics.configure(analyticsConfig);

require('vue-tour/dist/vue-tour.css');

Vue.config.productionTip = false;
Vue.component('icon', Icon);
Vue.component('vector', Vector);
Vue.component('Datepicker', Datepicker);
Vue.component('downloadCsv', JsonCSV);
Vue.component('v-select', vSelect);
Vue.directive('debounce', debounce);
Vue.use(VueMoment);
Vue.use(Router);
Vue.use(BootstrapVue);
Vue.use(Vue2Filters);
Vue.use(VueTour);
Vue.use(VueClipboard);
Vue.use(VuejsDialog);
Vue.use(VueLazyLoad);
Vue.use(lodash);
Vue.use(Toasted, {
    position: 'bottom-right',
    duration: 5000,
    containerClass: 'dc-toast',
    iconPack: 'fontawesome'
});
Vue.use(Timeago, {
    name: 'Timeago',
    locale: 'en'
});

let req = require.context('./modules/', true, /^\.\/[^\/]+\/router\.js$/);

let moduleRoutes = req.keys().map(key => req(key));
const router = new Router({
    mode: 'history',
    base: '/',
    routes: [
        {
            path: '/',
            component: () => import('@dash/components/Layout/Construct'),
            meta: { requiresAuth: true },
            children: moduleRoutes
        },
        {
            path: '/loading',
            component: () => import('@dash/components/Layout/Loading'),
            beforeEnter: (to, from, next) => {
                if (
                    dataResource.environment !== 'demo' &&
                    dataResource.environment !== 'local'
                ) {
                    next('/login');
                    return;
                }
                Auth.currentAuthenticatedUser()
                    .then(user => {
                        // TODO if not confirmed, direct to confirmation page
                        // TODO if already loaded redirect here
                        next();
                    })
                    .catch(err => {
                        next('/login');
                    });
            }
        },
        {
            path: '/login',
            component: () => import('@dash/components/Layout/Login'),
            beforeEnter: (to, from, next) => {
                Auth.currentAuthenticatedUser()
                    .then(user => {
                        next('/');
                    })
                    .catch(err => {
                        // TODO if not confirmed, direct to confirmation page
                        next();
                    });
            },
            children: [
                {
                    path: '',
                    name: 'login',
                    component: () =>
                        import('@dash/components/Layout/Login/Credentials')
                },
                {
                    path: 'signup',
                    component: () =>
                        import('@dash/components/Layout/Login/SignUp')
                },
                {
                    path: 'forgot',
                    component: () =>
                        import('@dash/components/Layout/Login/ForgotPass')
                },
                {
                    path: 'singleSignOn',
                    component: () =>
                        import('@dash/components/Layout/Login/SingleSignOn')
                },
                {
                    path: 'reset',
                    component: () =>
                        import('@dash/components/Layout/Login/ResetPass')
                }
            ]
        },
        {
            path: '/logout',
            beforeEnter: (to, from, next) => {
                Auth.signOut()
                    .then(() => next('/login'))
                    .catch(err => console.error(err));
            }
        },
        {
            path: '*',
            name:'notfound',
            component: () => import('@dash/components/Layout/NotFound'),
            meta: { requiresAuth: true }
        }
    ]
});

function bindHubListener() {
    // Used for SSO authentication, refreshes page once authenticated by oauth
    Hub.listen("auth", ({ payload: { event, data } }) => {
        switch (event) {
            case "signIn":
                Auth.currentAuthenticatedUser()
                    .then(user => {
                        router.push({name: 'emptyPage'});
                    })
                    .catch(err => {
                        console.error(err)
                    });
                break;
        }
    });
}

router.beforeEach((to, from, next) => {
    if(!(Object.values(Amplify.configure())).length) {
        Amplify.configure(userUtil.initAuth(to));

        if((Auth.configure()).oauth) {
            bindHubListener();
        }
    }

    /* Make sure the user is authenticated with our software */
    if (to.matched.some(record => record.meta.requiresAuth)) {
        Auth.currentAuthenticatedUser()
            .then(async user => {
                /* Check to make sure the user has permissions to this module */
                let x = to.matched.find(record => record.meta.module);
                let overrides = ['dash', 'profile'];
                if (x) {
                    dataResource.payload().then(payload => {
                        if (
                            payload.modules.indexOf(x.meta.module) === -1 &&
                            overrides.indexOf(x.meta.module) === -1
                        ) {
                            next('/');
                        }
                    });
                }

                // Verify if user has MFA enabled if required 
                if(userUtil.isMFARequired(user)) {
                    const perf = await Auth.getPreferredMFA(user);
                    if(perf === 'NOMFA' && to.path !== '/profile') {
                        next({name: 'profile', params: {redirect: to}, query: {require: 'mfa'}});
                    }
                }

                // dynamically assigning landing page on base url
                if(to.path === '/') {
                    dataResource.payload().then(payload => {
                        let landingPageModuleKey = payload.landingPageModuleKey;
                        if(landingPageModuleKey) {
                            // suppressing navigation error as the skipping of EmptyPage component is intentional
                            router.push({ path: MODULE_INFO[landingPageModuleKey].url }).catch(() => {});
                        } else {
                            // if no landingPageModuleKey exists visit EmptyPage
                            next();
                        }
                    });
                } else {
                    next();
                }
            })
            .catch(err => {
                console.error(err);
                next({name: 'login', params: {redirect: to}});
            });
    } else {
        // dynamically assigning landing page on base url
        if(to.path === '/') {
            dataResource.payload().then(payload => {
                let landingPageModuleKey = payload.landingPageModuleKey;
                if(landingPageModuleKey) {
                    // suppressing navigation error as the skipping of EmptyPage component is intentional
                    router.push({ path: MODULE_INFO[landingPageModuleKey].url }).catch(() => {}); 
                } else {
                    // if no landingPageModuleKey exists after login visit EmptyPage
                    router.push({ name: 'emptyPage' }).catch(() => {});
                }
            });
        } else {
            next();
        }
    }
});

/* eslint-disable no-new */
new Vue({
    el: '#app',
    router: router,
    template: '<App/>',
    components: { App }
});



// WEBPACK FOOTER //
// ./src/main.js